import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { PRESCRIBER_STATUS_COLORS } from '@europrocurement/flexy-components/assets';
import { InformationChip } from '../../atoms/InformationChip';
import { CustomAvatar } from '../CustomAvatar';

const STATUSES_COLORS = {
    1: 'warning', // Prospection - orange
    2: 'success', // Actif - vert
    3: PRESCRIBER_STATUS_COLORS.REGULAR, // Régulier - bleu
    4: PRESCRIBER_STATUS_COLORS.DROPOUT, // Abandoniste - violet
    5: PRESCRIBER_STATUS_COLORS.NOT_INTERESTED, // Pas intéressé - rose
    6: 'error', //	Perdu - rouge
    999: 'secondary', // Autre - gris
};

type Status = {
    id: keyof typeof STATUSES_COLORS;
    label: string;
};

export type CustomerServiceWidgetProps = {
    sector: string;
    firstName: string;
    lastName: string;
    userIdentifier?: string;
    status?: Status;
    avatar?: string;
    // user Id as ref for onClick function
    // eslint-disable-next-line react/no-unused-prop-types
    userId?: number;
    onClick?: () => void;
    fullNameOnClick?: () => void;
    fullNameHoverIcon?: IconName;
};

export const CustomerServiceWidget: React.FunctionComponent<CustomerServiceWidgetProps> =
    function ({
        sector,
        firstName,
        lastName,
        status,
        avatar,
        userIdentifier,
        onClick = () => {},
        fullNameOnClick,
        fullNameHoverIcon,
    }) {
        const { palette } = useTheme();
        const [fullNameHover, setFullNameHover] = useState<boolean>(false);

        const sectorStyle = {
            opacity: 0.7,
            fontWeight: '500',
            marginBottom: '15px',
        };
        const wrapperSx = {
            display: 'flex',
            alignItems: 'center',
            columnGap: '15px',
        };

        const nameWrapper = {
            display: 'flex',
            alignItems: 'center',
            cursor: fullNameOnClick ? 'pointer' : 'default',
        };

        const nameStyle = {
            opacity: 0.6,
            fontWeight: '400',
            paddingX: '3px',
        };

        const iconSx = {
            opacity: 0.6,
            fontWeight: '400',
            fontSize: '12px',
            color: palette.grey[600],
        };

        const statusWrapper = {
            marginTop: '18px',
        };

        const avatarStyle = {
            alignSelf: 'flex-start',
            '&:hover': { cursor: 'pointer' },
        };

        return (
            <Box
                data-testid="CustomerServiceWidget-test-id"
                display="flex"
                flexDirection="column"
            >
                <Typography sx={sectorStyle}>{sector}</Typography>
                <Box
                    sx={wrapperSx}
                    display="flex"
                >
                    <CustomAvatar
                        firstName={firstName}
                        lastName={lastName}
                        keycloakId={userIdentifier}
                        sx={avatarStyle}
                        src={avatar}
                        onClick={onClick}
                    />
                    <Box
                        display="flex"
                        flexDirection="column"
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={nameWrapper}
                            onClick={fullNameOnClick}
                            onMouseEnter={() => setFullNameHover(true)}
                            onMouseLeave={() => setFullNameHover(false)}
                        >
                            <Typography sx={nameStyle}>{firstName}</Typography>
                            <Typography sx={nameStyle}>{lastName}</Typography>
                            {fullNameHover && fullNameHoverIcon && fullNameOnClick && (
                                <FaOptionIcon
                                    sx={iconSx}
                                    icon={['fasl', fullNameHoverIcon]}
                                />
                            )}
                        </Box>
                        {status && (
                            <Box sx={statusWrapper}>
                                <InformationChip
                                    color={STATUSES_COLORS[status.id]}
                                    label={status.label}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };
